// ── queue.js — очередь аудио, как «Далее» в приложениях для подкастов ────────
// Список id аудио-проектов, которые заиграют один за другим, когда закончится
// текущее. Хранится в localStorage и синхронизируется между устройствами
// (ключ в SYNC_KEYS settings.js). Текущий проект в очередь не входит: когда
// аудио доиграло, первый из очереди убирается из неё и начинает играть.

import { useEffect, useState } from 'react';
import { markSetting } from './settings';

const KEY = 'ac_queue';
const EVT = 'ac:queue-changed';
const MAX = 200;

export function getQueue() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => x != null && x !== '').map(String) : [];
  } catch { return []; }
}

export function setQueue(ids) {
  const seen = new Set();
  const list = ids.map(String).filter((id) => !seen.has(id) && seen.add(id)).slice(0, MAX);
  try {
    if (list.length) localStorage.setItem(KEY, JSON.stringify(list));
    else localStorage.removeItem(KEY);
    markSetting(KEY);
  } catch { /* приватный режим */ }
  window.dispatchEvent(new CustomEvent(EVT));
  return list;
}

export const inQueue = (id) => getQueue().includes(String(id));
export const addToQueue = (ids) => setQueue([...getQueue(), ...[].concat(ids).map(String)].filter((id, i, a) => a.indexOf(id) === i));
export const playNextInQueue = (id) => setQueue([String(id), ...getQueue().filter((x) => x !== String(id))]);
export const removeFromQueue = (id) => setQueue(getQueue().filter((x) => x !== String(id)));
export const clearQueue = () => setQueue([]);

// Достаёт первый проект из очереди, который ещё существует и не равен текущему.
// Пропавшие (удалённые) id выбрасываются по пути.
export function takeNext(projects, currentId) {
  const q = getQueue();
  const byId = new Map(projects.map((p) => [String(p.id), p]));
  while (q.length) {
    const id = q.shift();
    const p = byId.get(id);
    if (p && id !== String(currentId)) { setQueue(q); return p; }
  }
  setQueue(q);
  return null;
}

// Живой список: меняется при правках здесь, в другой вкладке и после синхронизации.
export function useQueue() {
  const [q, setQ] = useState(getQueue);
  useEffect(() => {
    const refresh = () => setQ(getQueue());
    const onSettings = (e) => { const k = e?.detail?.keys || []; if (!k.length || k.includes(KEY)) refresh(); };
    const onStorage = (e) => { if (!e.key || e.key === KEY) refresh(); };
    window.addEventListener(EVT, refresh);
    window.addEventListener('ac:settings-updated', onSettings);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVT, refresh);
      window.removeEventListener('ac:settings-updated', onSettings);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return q;
}
